import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Loader from 'apollo-react/components/Loader';
import MenuButton from 'apollo-react/components/MenuButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Info from 'apollo-react-icons/Info';
import Menu from 'apollo-react-icons/Menu';
import qs from 'query-string';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

import EntityHeaderInfo from './EntityHeaderInfo';
import RecreateTemplatesConfirmationModal from './RecreateTemplatesConfirmationModal';
import { setOpenEntityHistoryDialog } from '../../../actions/EntityHistoryActions';
import { exportEntity } from '../../../api/entity';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { HOME } from '../../../constants/Urls';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { equalityFn } from '../../../form-designer/utils/form-manager.utils';
import { getUITA } from '../../../utils/commonUtils';
import {
  deleteEntity,
  publishEntityDesigner,
  saveEntityDesigner,
  validateEntityDesigner,
} from '../../actions/entity-designer.actions';
import {
  ENTITY_RELEASE_TYPE_CUSTOM,
  ENTITY_RELEASE_TYPE_PILGRIM,
  ENTITY_STATUS_INWORKS,
  ENTITY_STATUS_NONE,
  ENTITY_STATUS_PUBLISHED,
  ENTITY_VERSION_ONE,
  HEADER_ITEM_DEFAULT_LABEL,
} from '../../constants/entity-designer.constants';
import {
  getEntityInfo,
  getIsImportMode,
  getPublishDisabled,
  getSaveDisabled,
} from '../../selectors/entity-designer.selectors';
import {
  getEntityStatusLabel,
  getReleaseVersion,
  modifyDate,
} from '../../utils/entity-designer.utils';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px',
    '& ul': {
      marginLeft: '15px',
    },
    marginLeft: 15,
  },
  entityText: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  entityTextContainer: {
    marginRight: 55,
    margin: 5,
    display: 'flex',
    alignItems: 'start',
  },
  entityTextTitleContainer: {
    marginRight: 10,
  },
  entityToolTip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  entityTextTitle: {
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    color: 'var(--black)',
  },
  dotLegend: {
    display: 'flex',
    height: 24,
    marginRight: 10,
  },
  uiChanges: {
    color: 'orange',
  },
  deltaChanges: {
    color: 'blue',
  },
  addedChanges: {
    color: 'green',
  },
  titleWrapper: {
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  hiddenMenuButton: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
});

const Header = ({ setSelectedTab }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const entity = useSelector(getEntityInfo, equalityFn);
  const isImportMode = useSelector(getIsImportMode);
  const isSaveDisabled = useFormSelector(getSaveDisabled);
  const isPublishDisabled = useFormSelector(getPublishDisabled);
  const qsParams = qs.parse(location.search) || {};
  const isNewEntity = qsParams.isNewEntity;

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [isRecreateTemplatesModalOpened, setIsRecreateTemplatesModalOpened] = useState(false);
  const shouldDisplayActionButton = !isNewEntity && !isImportMode;

  const isActiveEntity = [
    ENTITY_STATUS_INWORKS,
    ENTITY_STATUS_PUBLISHED,
    ENTITY_STATUS_NONE,
  ].includes(entity.Status);

  const redirectToPrevPage = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push(`${HOME}`);
    }
  };

  const redirectToAnotherEntity = ({ itmId }) => {
    history.replace(`/entity-editor?itmId=${itmId}&standaloneLayout=true`);
  };

  const handleCancel = () => {
    redirectToPrevPage();
  };

  const handleSave = async () => {
    dispatch(validateEntityDesigner());

    await dispatch(saveEntityDesigner({ setIsLoading, setSelectedTab, redirectToAnotherEntity }));
    setIsLoading(false);
  };

  const handlePublish = async () => {
    setIsLoading(true);
    await dispatch(publishEntityDesigner({ redirectToPrevPage }));
    setIsLoading(false);
  };

  const handleDeleteEntity = async () => {
    setIsDeleteModalOpened(false);
    setIsLoading(true);
    await dispatch(deleteEntity({ redirectToPrevPage }));
    setIsLoading(false);
  };

  const handleExportEntity = async () => {
    await exportEntity({ entityId: entity.Id, fileName: entity.EntityName });
  };

  const displayEntityHistoryModal = () => {
    dispatch(setOpenEntityHistoryDialog(true));
  };

  const actionItems = [
    {
      text: translateString('Delete'),
      onClick: () => setIsDeleteModalOpened(true),
      hidden:
        entity.ReleaseType !== ENTITY_RELEASE_TYPE_CUSTOM ||
        entity.Status !== ENTITY_STATUS_INWORKS ||
        entity.Version !== ENTITY_VERSION_ONE,
    },
    {
      text: translateString('Export'),
      onClick: handleExportEntity,
      hidden: isNewEntity || isImportMode,
    },
    {
      text: translateString('View History'),
      onClick: displayEntityHistoryModal,
      hidden: !isActiveEntity || isNewEntity || isImportMode,
    },
    {
      text: translateString('Recreate Templates'),
      onClick: () => setIsRecreateTemplatesModalOpened(true),
      hidden:
        entity.ReleaseType === ENTITY_RELEASE_TYPE_PILGRIM ||
        entity.Status !== ENTITY_STATUS_PUBLISHED,
    },
  ];

  const sortedActionItems = actionItems.sort((a, b) => a.text.localeCompare(b.text));

  const menuItems = [
    ...sortedActionItems,
    {
      text: translateString('Cancel'),
      onClick: handleCancel,
    },
    {
      text: translateString('Save'),
      onClick: handleSave,
      disabled: isSaveDisabled && !isImportMode,
      hidden: !isActiveEntity,
    },
    {
      text: translateString('Publish'),
      onClick: handlePublish,
      disabled: isPublishDisabled,
      hidden: !isActiveEntity,
    },
  ];

  const entityArr = [
    { title: `Name: ${entity.EntityName}`, subtitle: '' },
    {
      title: `Last Modified Date: ${modifyDate(entity.ModifiedDate) ?? HEADER_ITEM_DEFAULT_LABEL}`,
      subtitle: '',
    },
    {
      title: `Status: ${getEntityStatusLabel(entity.Status)}`,
      subtitle: '',
    },
    { title: `Version Number: ${entity.Version}`, subtitle: '' },
    {
      title: `Release Version: ${getReleaseVersion(entity.ReleaseVersion)}`,
      subtitle: '',
    },
    { title: `Release Type: ${entity.ReleaseType ?? HEADER_ITEM_DEFAULT_LABEL}`, subtitle: '' },
  ];

  const deleteConfirmMessage = (
    <>
      {translateString('Do you really want to delete entity')} <b>{entity.EntityName}</b> {'?'}
    </>
  );

  const filteredItems = sortedActionItems.filter((action) => !action.hidden);

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.titleWrapper} item xs={8}>
          <span className={classes.entityToolTip}>
            {entity && (
              <>
                <Tooltip extraLabels={entityArr} placement="right" variant="light">
                  <IconButton aria-label="Info Icon">
                    <Info color="primary" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </span>
          <span className={classes.entityText} ui-ta={getUITA('entity')}>
            <EntityHeaderInfo />
          </span>
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <IconMenuButton
            id="menu"
            className={classes.hiddenMenuButton}
            menuItems={menuItems.filter((action) => !action.hidden)}
            aria-label="Menu Icon"
          >
            <Menu />
          </IconMenuButton>
          {shouldDisplayActionButton && (
            <MenuButton
              buttonText={translateString('Actions')}
              menuItems={filteredItems}
              className={`${classes.marginRight} ${classes.resetOverflow}`}
              disabled={filteredItems.length === 0}
            />
          )}
          <Button
            variant="text"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleCancel}
            ui-ta={getUITA('Editor Header Header Button', 'Cancel')}
          >
            {translateString('Cancel')}
          </Button>
          {isActiveEntity && (
            <>
              <Button
                variant="secondary"
                className={`${classes.marginRight} ${classes.resetOverflow}`}
                onClick={handleSave}
                disabled={isSaveDisabled && !isImportMode}
                ui-ta={getUITA('Entity Editor Header Button', 'Save')}
              >
                {translateString('Save')}
              </Button>
              <Button
                variant="primary"
                className={`${classes.marginRight} ${classes.resetOverflow}`}
                onClick={handlePublish}
                disabled={isPublishDisabled || isImportMode}
                ui-ta={getUITA('Entity Editor Header Button', 'Publish')}
              >
                {translateString('Publish')}
              </Button>
            </>
          )}
          {isLoading && <Loader isInner />}
        </Grid>
        <DeleteConfirmationModal
          message={deleteConfirmMessage}
          open={isDeleteModalOpened}
          onClose={() => setIsDeleteModalOpened(false)}
          onDeleteConfirm={handleDeleteEntity}
        />
        <RecreateTemplatesConfirmationModal
          isOpen={isRecreateTemplatesModalOpened}
          setIsOpen={setIsRecreateTemplatesModalOpened}
        />
      </Grid>
    </div>
  );
};

export default Header;
